import {
  getTenantWorldType,
  TENANT_CONFIG,
} from '@evefrontier/wallet-core/tenant'
import { bcs } from '@mysten/sui/bcs'
import { deriveObjectID } from '@mysten/sui/utils'
import { getSingletonObjectByType } from '../graphql/client'
import { Assemblies, State } from '../types'
import { getWorldType, TenantId } from './constants'

/**
 * Convert raw status variant string to State enum
 *
 * @category Utilities - Mapping
 */
export function parseStatus(statusVariant: string | undefined): State {
  if (!statusVariant) return State.NULL

  switch (statusVariant.toUpperCase()) {
    case 'ONLINE':
      return State.ONLINE
    case 'OFFLINE':
      return State.ANCHORED
    case 'ANCHORED':
      return State.ANCHORED
    case 'UNANCHORED':
      return State.UNANCHORED
    case 'DESTROYED':
      return State.DESTROYED
    default:
      return State.NULL
  }
}

/**
 * Get assembly type from Move object type tag
 * @param typeRepr - The Move object type tag
 * @returns The assembly type as an enum
 *
 * @category Utilities - Mapping
 */
export function getAssemblyType(typeRepr: string): Assemblies {
  if (typeRepr.includes('::storage_unit::StorageUnit')) {
    return Assemblies.SmartStorageUnit
  }
  if (typeRepr.includes('::turret::Turret')) {
    return Assemblies.SmartTurret
  }
  if (typeRepr.includes('::gate::Gate')) {
    return Assemblies.SmartGate
  }
  if (typeRepr.includes('::network_node::NetworkNode')) {
    return Assemblies.NetworkNode
  }
  if (typeRepr.includes('::assembly::Assembly')) {
    return Assemblies.Assembly
  }

  // Assembly type not found, return default Assembly
  return Assemblies.Assembly
}

// Cache for the Registry address, keyed by tenant
const objectRegistryAddressCache: Record<string, string> = {}

/**
 * Fetches the ObjectRegistry singleton address from the chain for a given tenant.
 * Uses the tenant's package ID from TENANT_CONFIG to look up the correct registry.
 * Caches the result per tenant to avoid repeated queries.
 *
 * If the tenant is not a predefined tenant such as in local development,
 * fallback to the local Eve World package ID.
 *
 * @category Utilities - Mapping
 */
export async function getRegistryAddress(
  tenant: string | TenantId,
): Promise<string> {
  if (objectRegistryAddressCache[tenant]) {
    return objectRegistryAddressCache[tenant]
  }

  // Configured tenants resolve the type tag from the MVR cache; unconfigured
  // (local) tenants use the env-driven world type.
  const registryType = TENANT_CONFIG[tenant as TenantId]
    ? getTenantWorldType(tenant as TenantId, 'object_registry::ObjectRegistry')
    : getWorldType('object_registry::ObjectRegistry')

  const result = await getSingletonObjectByType(registryType)

  const address = result.data?.objects?.nodes?.[0]?.address
  if (!address) {
    throw new Error(`ObjectRegistry not found for type: ${registryType}`)
  }

  objectRegistryAddressCache[tenant] = address
  return address
}

/**
 * Derives an object ID from an in-game item ID using the AssemblyRegistry.
 * Resolves the registry and TenantItemId type tags to their type-origin-correct
 * values via the shared MVR cache (wallet-core `getTenantWorldType`).
 *
 * If the tenant is not a predefined tenant such as in local development,
 * fallback to the local Eve World package ID.
 *
 * @param itemId - The in-game item ID
 * @param selectedTenant - The tenant identifier (e.g. "utopia", "stillness")
 * @returns The derived Sui object ID
 *
 * @category Utilities - Mapping
 */
export async function getObjectId(
  itemId: string,
  selectedTenant: string | TenantId,
): Promise<string> {
  const validTenantIds = Object.values(TenantId) as string[]
  if (!validTenantIds.includes(selectedTenant)) {
    console.warn(`Tenant "${selectedTenant}" might be a local tenant.`)
  }

  const tenant = selectedTenant as TenantId
  const registryAddress = await getRegistryAddress(tenant)
  // TenantItemId key type tag: MVR cache for configured tenants, env-driven
  // world type otherwise.
  const tenantItemIdType = TENANT_CONFIG[tenant]
    ? getTenantWorldType(tenant, 'in_game_id::TenantItemId')
    : getWorldType('in_game_id::TenantItemId')

  const bcsType = bcs.struct('TenantItemId', {
    item_id: bcs.u64(),
    tenant: bcs.string(),
  })
  const key = bcsType.serialize({ item_id: BigInt(itemId), tenant }).toBytes()

  const objectId = deriveObjectID(registryAddress, tenantItemIdType, key)

  return objectId
}
